import React from "react";
import { Trans, useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { PermissionService } from "src/services/permissionService/permissionService";
import { useDispatch, useSelector } from "src/store";
import { selectCurrentWorkspaceId } from "src/store/app";

import {
    useAssignPolicyToGroupsMutation,
    useGetPoliciesQuery,
    useGetPolicyByIdQuery,
    useUnassignPolicyFromGroupsMutation,
} from "@dashboard/policies/api";
import PolicyCommandsTable from "@dashboard/shared/components/PolicyCommands/PolicyCommandsTable";
import { PaneElement } from "@dashboard/shared/components/SidePanel/shared/PaneElement";
import { useCurrentSubscriptionExpired, useCurrentWorkspace } from "@dashboard/workspaces/api/helpers";
import { selectPaneStatus, setGroupPolicy } from "@devices/store";
import { Group } from "@devices/types";
import { Box } from "@mui/material";
import { skipToken } from "@reduxjs/toolkit/query";
import { CustomAlert } from "@shared/CustomAlert/CustomAlert";
import { SecondaryButton } from "@shared/CustomButton";
import { withExpiredWrapper } from "@shared/ExpiredWrapper";
import { SelectField } from "@shared/form/SelectField/SelectField";
import { SingleSelectFieldProps } from "@shared/form/SelectField/SelectField.types";
import { TextLink } from "@shared/TextLink";
import { showErrorToast, showSuccessToast } from "@shared/toasts/Toasts";
import { getErrorMessage } from "@shared/toasts/useQueryErrorToast";

function PolicyTab(group: Group): React.JSX.Element {
    const { t } = useTranslation(["sidepanels", "common"]);
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const workspaceId = useSelector(selectCurrentWorkspaceId);
    const { data: workspace = null } = useCurrentWorkspace();
    const { groupAbility, workspaceAbility } = PermissionService();
    const isSubscriptionExpired = useCurrentSubscriptionExpired();

    const ability = groupAbility(group, workspace);
    const cannotChangePolicy = ability.cannot("assign", "Policy") || ability.cannot("unassign", "Policy");
    const cannotUpdatePolicy = workspaceAbility(workspace).cannot("update", "Policy");
    const cannotCreatePolicy = workspaceAbility(workspace).cannot("create", "Policy");

    const {
        data: policiesResponse,
        isLoading: isLoadingPolicies,
        isError: isPoliciesError,
    } = useGetPoliciesQuery(workspaceId !== null && !isSubscriptionExpired ? { workspaceId } : skipToken);
    const policies = policiesResponse?.list ?? [];

    const {
        data: assignedPolicyResponse,
        isLoading: isLoadingAssignedPolicy,
        isError: isAssignedPolicyError,
    } = useGetPolicyByIdQuery(group.policyId && !isSubscriptionExpired ? { id: group.policyId } : skipToken);

    const loadFailed = isPoliciesError || isAssignedPolicyError;

    const [assignPolicyToGroups, { isLoading: isAssigning }] = useAssignPolicyToGroupsMutation();
    const [unassignPolicyFromGroups, { isLoading: isUnassigning }] = useUnassignPolicyFromGroupsMutation();

    const policyOptions = policies.map((policy) => ({ label: policy.name, value: policy.id }));

    const revertOptimisticPolicy = () => {
        dispatch(
            setGroupPolicy({
                groupId: group.id,
                policyId: group.policyId,
                policy: group.policy,
                policyHash: group.policyHash,
            }),
        );
    };

    const handlePolicyChange: SingleSelectFieldProps["onChange"] = async (_event, selectedOption) => {
        if (!selectedOption) {
            if (!group.policyId) {
                return;
            }

            const unassignedPolicyId = group.policyId;
            dispatch(setGroupPolicy({ groupId: group.id, policyId: null, policy: null, policyHash: null }));

            const result = await unassignPolicyFromGroups({ id: unassignedPolicyId, groupIds: [group.id] });
            if ("error" in result) {
                showErrorToast(getErrorMessage(result.error));
                revertOptimisticPolicy();
                return;
            }
            showSuccessToast(t("sidepanels:groupPanel.policyTab.toast.policyUnassigned"));
            return;
        }

        if (typeof selectedOption.value !== "string") {
            return;
        }

        const selectedPolicy = policies.find((policy) => policy.id === selectedOption.value);
        if (!selectedPolicy) {
            return;
        }

        dispatch(
            setGroupPolicy({
                groupId: group.id,
                policyId: selectedPolicy.id,
                policy: selectedPolicy.content,
                policyHash: selectedPolicy.hash,
            }),
        );

        const result = await assignPolicyToGroups({ id: selectedPolicy.id, groupIds: [group.id] });
        if ("error" in result) {
            showErrorToast(getErrorMessage(result.error));
            revertOptimisticPolicy();
            return;
        }
        showSuccessToast(t("sidepanels:groupPanel.policyTab.toast.policyAssigned"));
    };

    const getPolicyValue = () => {
        if (!group.policyId) {
            return undefined;
        }

        const name = policies.find((policy) => policy.id === group.policyId)?.name ?? assignedPolicyResponse?.model?.name;
        return name ? { label: name, value: group.policyId } : undefined;
    };

    const getDisabledTooltip = () => {
        if (isPoliciesError) {
            return t("common:error.policies.failedToGetPolicies");
        }
        if (isAssignedPolicyError) {
            return t("common:error.policies.failedToGetPolicy");
        }
        return cannotChangePolicy ? t("common:tooltip.cannotManagePolicies") : "";
    };

    const getOpenPolicyTooltip = () => {
        if (!group.policyId) {
            return t("sidepanels:groupPanel.policyTab.tooltip.noPolicyAssigned");
        }
        return cannotUpdatePolicy
            ? t("sidepanels:groupPanel.policyTab.tooltip.showPolicy")
            : t("sidepanels:groupPanel.policyTab.tooltip.editPolicy");
    };

    const getNoPolicyMessage = () => {
        if (loadFailed || cannotChangePolicy) {
            return t("sidepanels:groupPanel.policyTab.empty.noPolicy");
        }
        if (policies.length) {
            return t("sidepanels:groupPanel.policyTab.empty.choosePolicy");
        }
        if (cannotCreatePolicy) {
            return t("sidepanels:groupPanel.policyTab.empty.noPolicy");
        }
        return (
            <Trans
                t={t}
                i18nKey="sidepanels:groupPanel.policyTab.empty.createPolicy"
                components={{
                    // Not named "link": i18next parses <link> as a void element and would leave the label outside
                    // the anchor.
                    policiesLink: (
                        <TextLink
                            component="button"
                            type="button"
                            onClick={() => void navigate("/dashboard/policies")}
                            tooltip={t("sidepanels:groupPanel.policyTab.tooltip.goToPoliciesPage")}
                        />
                    ),
                }}
            />
        );
    };

    const paneStatus = useSelector((state) => selectPaneStatus(state));
    if (paneStatus === "pending" || isLoadingPolicies || isLoadingAssignedPolicy) {
        return <PaneElement.Loader />;
    }

    return (
        <PaneElement.Container>
            <PaneElement.Header>{t("sidepanels:groupPanel.policyTab.groupPolicy")}</PaneElement.Header>
            {loadFailed ? (
                <Box style={{ paddingBottom: 16 }}>
                    <CustomAlert severity="error">
                        {isPoliciesError
                            ? t("common:error.policies.failedToGetPolicies")
                            : t("common:error.policies.failedToGetPolicy")}
                    </CustomAlert>
                </Box>
            ) : null}
            <Box style={{ display: "flex", alignItems: "flex-end", gap: 8, paddingBottom: 16 }}>
                <SelectField
                    label={t("sidepanels:groupPanel.policyTab.picker.label")}
                    value={getPolicyValue()}
                    onChange={handlePolicyChange}
                    options={policyOptions}
                    disabled={loadFailed || isAssigning || isUnassigning || cannotChangePolicy}
                    disabledTooltip={getDisabledTooltip()}
                    loading={isAssigning || isUnassigning}
                    placeholder={t("sidepanels:groupPanel.policyTab.picker.placeholder")}
                    disableClearable={false}
                    fullWidth
                    searchable
                />
                <SecondaryButton
                    onClick={() => void navigate(`/dashboard/policies?policyId=${group.policyId}`)}
                    disabled={!group.policyId}
                    tooltipProps={{ title: getOpenPolicyTooltip() }}
                >
                    <i className="fa-solid fa-pencil" />
                </SecondaryButton>
            </Box>
            {group.policyId ? (
                <PolicyCommandsTable content={group.policy ?? []} readOnly />
            ) : (
                <Box style={{ display: "flex", alignItems: "center", justifyContent: "center", flex: 1, minHeight: 0 }}>
                    <PaneElement.NoData message={getNoPolicyMessage()} />
                </Box>
            )}
        </PaneElement.Container>
    );
}
export default withExpiredWrapper(PolicyTab);
